/**
 * Session Cache Service
 * 
 * Stores captured images and questionnaire answers in sessionStorage
 * to allow users to resume if they accidentally close the tab,
 * and provides a way to start a new session.
 */

const KEYS = {
    CAPTURED_IMAGES: 'thiet_chuan_session_images',
    ANSWERS: 'thiet_chuan_session_answers',
    ANALYSIS_TYPE: 'thiet_chuan_session_type',
    TIMESTAMP: 'thiet_chuan_session_time',
};

// Save captured images to session
export function saveCapturedImages(images) {
    try {
        sessionStorage.setItem(KEYS.CAPTURED_IMAGES, JSON.stringify(images));
        sessionStorage.setItem(KEYS.TIMESTAMP, Date.now().toString());
    } catch (e) {
        console.warn('Failed to save images to session:', e);
    }
}

// Save questionnaire answers to session
export function saveAnswers(answers) {
    try {
        sessionStorage.setItem(KEYS.ANSWERS, JSON.stringify(answers));
        sessionStorage.setItem(KEYS.TIMESTAMP, Date.now().toString());
    } catch (e) {
        console.warn('Failed to save answers to session:', e);
    }
}

// Save analysis type to session
export function saveAnalysisType(type) {
    try {
        sessionStorage.setItem(KEYS.ANALYSIS_TYPE, type);
    } catch (e) {
        console.warn('Failed to save analysis type to session:', e);
    }
}

// Get cached images
export function getCachedImages() {
    try {
        const data = sessionStorage.getItem(KEYS.CAPTURED_IMAGES);
        return data ? JSON.parse(data) : null;
    } catch (e) {
        console.warn('Failed to get cached images:', e);
        return null;
    }
}

// Get cached answers
export function getCachedAnswers() {
    try {
        const data = sessionStorage.getItem(KEYS.ANSWERS);
        return data ? JSON.parse(data) : null;
    } catch (e) {
        console.warn('Failed to get cached answers:', e);
        return null;
    }
}

// Get cached analysis type
export function getCachedAnalysisType() {
    try {
        return sessionStorage.getItem(KEYS.ANALYSIS_TYPE);
    } catch (e) {
        return null;
    }
}

// Get session timestamp
export function getSessionTimestamp() {
    try {
        const ts = sessionStorage.getItem(KEYS.TIMESTAMP);
        return ts ? parseInt(ts, 10) : null;
    } catch (e) {
        return null;
    }
}

// Check if there's a valid session (not older than 1 hour)
export function hasValidSession() {
    const timestamp = getSessionTimestamp();
    if (!timestamp) return false;

    const oneHour = 60 * 60 * 1000;
    const isRecent = (Date.now() - timestamp) < oneHour;

    const images = getCachedImages();
    const hasData = images && Object.keys(images).length > 0;

    return isRecent && hasData;
}

// Clear session (for "New Session" button)
export function clearSession() {
    try {
        sessionStorage.removeItem(KEYS.CAPTURED_IMAGES);
        sessionStorage.removeItem(KEYS.ANSWERS);
        sessionStorage.removeItem(KEYS.ANALYSIS_TYPE);
        sessionStorage.removeItem(KEYS.TIMESTAMP);
    } catch (e) {
        console.warn('Failed to clear session:', e);
    }
}

// Get session summary for display
export function getSessionSummary() {
    const images = getCachedImages();
    const answers = getCachedAnswers();
    const timestamp = getSessionTimestamp();

    if (!images || !timestamp) return null;

    return {
        imageCount: Object.keys(images).length,
        answerCount: answers ? Object.keys(answers).length : 0,
        timestamp: new Date(timestamp),
        timeAgo: getTimeAgo(timestamp),
    };
}

// Helper: format time ago
function getTimeAgo(timestamp) {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);

    if (seconds < 60) return 'vừa xong';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} phút trước`;
    return `${Math.floor(seconds / 3600)} giờ trước`;
}

export default {
    saveCapturedImages,
    saveAnswers,
    saveAnalysisType,
    getCachedImages,
    getCachedAnswers,
    getCachedAnalysisType,
    hasValidSession,
    clearSession,
    getSessionSummary,
};
